import { useState } from 'react'
import { catalog as api } from '@/api/endpoints'
import type { Flavor } from '@/api/types'
import { Err } from '@/components/Err'
import { NumberField } from '@/components/NumberField'
import { Button } from '@/components/ui/button'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FREE } from '@/domain/words'
import { size } from '@/ui/format'

// 사양의 창 둘(만들기·삭제) — 목록(`Catalog.tsx`)과 상세(`FlavorView.tsx`)가 함께 쓴다.
//
// **만들 때 정하고, 뒤에는 이름만 고친다**(F-63) — 이름은 상세의 제목을 눌러 그 자리에서 고친다. 임대가 사양을 가리키는 키라
// 쓰고 있는 사양은 내리지(`신청 불가`) 숫자를 바꾸지 않는다. 그 비대칭을 문장으로 적지 않는다: 고칠 수 있는 칸만 선다.
// **자리는 창에서 적지 않는다**(F-105) — 목록에서 끌어 옮기고, 새 사양은 맨 뒤로 간다.

const SELECT = 'h-9 w-full rounded-md border border-input bg-background px-3 text-base sm:text-[13px]'

export function FlavorCreateForm({ steps, onDone }: { steps: number[]; onDone: () => void }) {
  const [name, setName] = useState('')
  const [display, setDisplay] = useState('')
  const [cores, setCores] = useState('')
  const [memoryGb, setMemoryGb] = useState('')
  const [diskGb, setDiskGb] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const ok = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name) && display.trim().length > 0
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      // 비운 자원은 싣지 않는다 — 생략이 곧 자유다(F-62). 메모리만 GB 로 적고 MB 로 간다.
      await api.createFlavor({
        name,
        display_name: display.trim(),
        ...(cores === '' ? {} : { cores: Number(cores) }),
        ...(memoryGb === '' ? {} : { memory_mb: Number(memoryGb) * 1024 }),
        ...(diskGb === '' ? {} : { disk_gb: Number(diskGb) }),
      })
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <DialogHeader>
        <DialogTitle>사양 생성</DialogTitle>
      </DialogHeader>
      <div className="grid gap-1.5">
        <Label htmlFor="flavor-name">이름</Label>
        <Input id="flavor-name" value={name} placeholder="gpu-large" maxLength={63} autoComplete="off" disabled={busy} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="flavor-display">표시 이름</Label>
        <Input id="flavor-display" value={display} maxLength={200} disabled={busy} onChange={(e) => setDisplay(e.target.value)} />
      </div>
      <NumberField id="flavor-cores" label="코어" value={cores} placeholder={FREE} busy={busy} onChange={setCores} />
      <NumberField id="flavor-memory" label="메모리 (GB)" value={memoryGb} placeholder={FREE} busy={busy} onChange={setMemoryGb} />
      <div className="grid gap-1.5">
        <Label htmlFor="flavor-disk">디스크</Label>
        <select id="flavor-disk" className={SELECT} value={diskGb} disabled={busy} onChange={(e) => setDiskGb(e.target.value)}>
          <option value="">{FREE}</option>
          {steps.map((gb) => (
            <option key={gb} value={String(gb)}>
              {size(gb)}
            </option>
          ))}
        </select>
      </div>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onDone}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !ok}>
          {busy ? '만드는 중' : '만들기'}
        </Button>
      </DialogFooter>
    </form>
  )
}

/** 표시 이름만 고친다. 신청 가능·불가는 상세의 칩이 바꾼다. */
/**
 * 고른 사양들의 삭제. 대상의 목록을 보이고 묻는다 — 하나여도 같은 모양이다.
 * 하나씩 지우고 실패한 사양만 남아 같은 버튼이 곧 재시도다. 그 사양으로 만든 VM 과는 무관하다(F-107) —
 * 실패는 이미 지워졌거나(목록을 다시 읽는다) 서버·연결의 고장뿐이다.
 */
export function FlavorDeleteForm({
  flavors,
  onClose,
  onDone,
  onRefused,
}: {
  flavors: Flavor[]
  onClose: () => void
  onDone: () => void
  onRefused: () => void
}) {
  const [left, setLeft] = useState<string[]>(() => flavors.map((f) => f.id))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const targets = flavors.filter((f) => left.includes(f.id))
  const remove = async () => {
    setBusy(true)
    setError(null)
    let first: unknown = null
    const failed = new Set<string>()
    for (const f of targets) {
      try {
        await api.deleteFlavor(f.id)
      } catch (e) {
        if (first === null) first = e
        failed.add(f.id)
      }
    }
    setBusy(false)
    if (first !== null) {
      setError(first)
      setLeft((ids) => ids.filter((id) => failed.has(id)))
      onRefused()
      return
    }
    onDone()
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>사양 삭제</DialogTitle>
      </DialogHeader>
      <ul aria-label="삭제할 사양" className="max-h-60 overflow-y-auto rounded-md border">
        {targets.map((f) => (
          <li key={f.id} className="truncate px-3 py-1.5 text-[13px] font-medium">
            {f.display_name}
          </li>
        ))}
      </ul>
      <p className="text-[13px]">사양이 목록에서 사라집니다.</p>
      <Err error={error} />
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button variant="destructive" disabled={busy} onClick={() => void remove()}>
          {busy ? '삭제 중' : '삭제'}
        </Button>
      </DialogFooter>
    </>
  )
}
